<script setup lang="ts">
import { onMounted, onUnmounted, getCurrentInstance, ref, computed } from 'vue';
import type { PropType } from 'vue';
import useUi2FormElement from '@mop/ui2/composables/useUi2FormElement';
import type { Ui2RadioItem } from '@mop/ui2/types';

defineOptions({
  name: 'Ui2Radio',
  inheritAttrs: false,
});

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    required: true,
  },
  items: {
    type: Array as PropType<Ui2RadioItem[]>,
    default: () => [],
  },
  checkCircle: {
    type: Boolean,
    default: false,
  },
  rules: {
    type: Array,
    default: () => [],
  },
  vertical: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const { validationErrorsRef, register, unregister, validate } = useUi2FormElement(props);
defineExpose({ validate });
const hasErrorRef = computed(() => validationErrorsRef.value.length > 0);
const inputElement = ref<HTMLInputElement>();

function handleChange(event: Event) {
  emit('update:modelValue', (event.target as HTMLInputElement).value);
  props.rules.length && validate();
}

onMounted(() => {
  register(getCurrentInstance());
});
onUnmounted(() => {
  unregister(getCurrentInstance());
});
</script>

<template>
  <div
    v-if="items.length"
    :class="[
      'ui-radio-wrapper',
      {
        'ui-radio-wrapper--check': checkCircle,
        'ui-radio-wrapper--error': hasErrorRef,
        'ui-radio-wrapper--disabled': disabled,
        'ui-radio-wrapper--vertical': vertical,
      },
      $attrs['class'],
    ]"
  >
    <div class="ui-radio__list">
      <div
        v-for="(item, index) in items"
        :key="index"
        :class="[
          'ui-radio__list-item',
          {
            'ui-radio__list-item--no-content': !item.text && !item.helperText,
          },
        ]"
      >
        <div
          :class="[
            'ui-radio',
            {
              'ui-radio--selected': modelValue === item.value,
            },
          ]"
        >
          <input
            v-bind="$attrs"
            :id="`${name}-${item.value}`"
            ref="inputElement"
            type="radio"
            class="ui-radio__input"
            :name="name"
            :value="item.value"
            :checked="modelValue === item.value"
            :disabled="disabled"
            tabindex="0"
            @change="handleChange"
          />
          <span class="ui-radio__icon">
            <Ui2Icon
              :key="checkCircle ? 'check' : 'radio'"
              class="ui-radio__icon-svg"
              :name="checkCircle ? 'check' : 'radio'"
              :width="checkCircle ? 20 : 16"
              :height="checkCircle ? 20 : 16"
            />
          </span>
        </div>
        <div v-if="item.text || item.helperText">
          <label v-if="item.text" :for="`${name}-${item.value}`" class="ui-radio__label">
            <span class="ui-radio__label-content">{{ item.text }}</span>
            <slot name="suffix" />
          </label>
          <div v-if="item.helperText" class="ui-radio__helper-text">
            {{ item.helperText }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="hasErrorRef" class="ui-radio__error">
      <Ui2Notification type="inline" :show-icon="false" :show-close-button="false" status="error">
        {{ validationErrorsRef[0] }}
      </Ui2Notification>
    </div>
  </div>
</template>

<style lang="scss">
$radio-gap: $space-16;
$radio-size: 24px;
$radio-width: 1px;

.ui-radio__list {
  display: flex;
  gap: $radio-gap;
}

.ui-radio-wrapper--vertical {
  .ui-radio__list {
    flex-direction: column;
  }
}

.ui-radio__list-item {
  display: grid;
  flex: 1;
  align-items: flex-start;
  text-align: left;
  gap: $radio-gap;
  grid-template-columns: $radio-size 1fr;
}

.ui-radio__list-item--no-content {
  gap: 0;
}

.ui-radio__label {
  @include v2-text-style(md);
  display: flex;
  gap: $space-8;
  cursor: pointer;
  color: $color-text-body-primary;
}

.ui-radio__label-content {
  flex: 1;
}

.ui-radio {
  position: relative;
}

.ui-radio__icon {
  width: $radio-size;
  height: $radio-size;
  border-radius: $border-radius-rounded-full;
  border: $radio-width solid $color-border-primary;
  background: $color-surface-primary;
  transition: all $animation-duration-short $animation-type-standard;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: $color-border-action-hover;
}

.ui-radio__input {
  position: absolute;
  cursor: pointer;
  opacity: 0;
  width: 100%;
  height: 100%;

  &:disabled {
    cursor: not-allowed;
  }

  &:hover ~ .ui-radio__icon {
    border-color: $color-border-action-hover;
  }

  &:disabled ~ .ui-radio__icon {
    pointer-events: none;
    opacity: $v2-disabled-opacity;
  }

  &:checked ~ .ui-radio__icon {
    border-color: $color-border-action-hover;
  }

  &:focus-visible ~ .ui-radio__icon {
    border-color: $color-border-focus;
    box-shadow:
      0px 0px 0px 2px $color-core-white,
      0px 0px 0px 4px $color-border-focus;
  }

  &:focus-visible:checked ~ .ui-radio__icon {
    border-color: $color-border-focus;
  }
}

.ui-radio-wrapper--check {
  .ui-radio__input {
    &:checked ~ .ui-radio__icon {
      background: $color-surface-action-1;
      color: $color-text-on-action;
    }
  }
}

.ui-radio-wrapper--disabled {
  cursor: not-allowed;

  .ui-radio__label {
    cursor: not-allowed;
  }
}

.ui-radio-wrapper--error {
  .ui-radio__icon {
    border-color: $color-border-error;
  }

  .ui-radio__input {
    &:focus-visible ~ .ui-radio__icon {
      border-color: $color-border-error;
      box-shadow:
        0px 0px 0px 2px $color-core-white,
        0px 0px 0px 4px $color-border-error;
    }
  }
}

.ui-radio__helper-text {
  @include v2-text-style(sm);
  color: $color-text-body-secondary;
}

.ui-radio__error {
  padding-left: calc($radio-size + $space-16);
}

.ui-radio-wrapper:not(.ui-radio-wrapper--check) {
  .ui-radio__icon-svg {
    scale: 0;
    opacity: 0;
    transition: all $animation-duration-short $animation-type-standard;
  }
  .ui-radio--selected {
    .ui-radio__icon-svg {
      scale: 1;
      opacity: 1;
    }
  }
}

.ui-radio-wrapper--check {
  .ui-radio__icon {
    transition: all $animation-duration-short $animation-type-standard;
  }

  .ui-radio__icon-svg {
    color: $color-text-on-action;
  }
}
</style>
